import React from 'react';

import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import type {
  CourseSessionCurrentContentResponse,
  CourseSessionQuizResponse,
} from 'src/api/ProactApiClient';
import {CourseDefinitionQuizQuestionTypeEnum} from 'src/api/ProactApiClient';
import {ContentTitle, Subtitle} from 'src/design/atoms/Typography';
import {Text} from 'src/design/atoms/Text';
import {Description} from 'src/design/atoms/Description';
import {QuestionAnswerResultIcon} from '../../../../../app/training/sessionIcons';
import {SessionAnswerMultiChoice} from './SessionAnswerMultiChoice';
import {SessionAnswerShowCorrectAnswer} from './SessionAnswerShowCorrectAnswer';
import {SessionAnswerSingleChoice} from './SessionAnswerSingleChoice';
import {GetQuestionInstructionLabel} from './sessionHelpers';

export interface TrackerActiveSessionQuizResultsProps {
  quizResults: CourseSessionQuizResponse;
  quizContent: CourseSessionCurrentContentResponse;
  onContinue: () => void;
  onExit: () => void;
}
export const TrackerActiveSessionQuizResults: React.FC<
  TrackerActiveSessionQuizResultsProps
> = ({quizResults, quizContent, onContinue, onExit}) => {
  const dQuiz = quizContent.contentDefinition!.quiz;
  const {
    scorePassPercentage,
    enableShowCorrectAnswersInResults,
    questions: dQuestions,
  } = dQuiz!;

  const {isPassed, earnedScore} = quizResults;
  const title =
    isPassed === true
      ? 'Great work, you passed.'
      : 'Unfortunately, you did not pass.';
  const body =
    isPassed === true
      ? "You successfully passed the quiz. You can review the quiz results below. When ready, click the 'Continue' button below to proceed with your training."
      : "You did not meet the minimum score to pass the quiz. You can review the quiz results below. Since you did not pass the quiz, you will need to attempt this training course again. When ready, click the 'Continue' button below to exit the training.";

  let totalScore = 0.0;
  (dQuestions?.map(i => i.scoreValue) ?? []).forEach(element => {
    totalScore += element;
  });
  const scorePercent = Math.round(((earnedScore ?? 0) / totalScore) * 100);

  const scoreColor = isPassed === true ? 'green' : 'red';
  const scoreline = `You earned ${earnedScore} points, or ${scorePercent}%, of ${totalScore} points. A percentage of ${scorePassPercentage}% was required to pass.`;

  const action = isPassed === true ? onContinue : onExit;
  return (
    <Box>
      <Subtitle text={title} />
      <Alert my="md" color={scoreColor}>
        {scoreline}
      </Alert>
      <Text>{body}</Text>
      {quizResults.questions?.map((sQuestion, index) => {
        const dQuestion = dQuestions?.filter(
          i => i.id == sQuestion.questionDefinitionId
        )[0];

        if (dQuestion === undefined) {
          return (
            <Text>
              {`Invalid definition for session question ${sQuestion.id}`}
            </Text>
          );
        }

        return (
          <React.Fragment key={sQuestion.id}>
            <Group gap="xs" align="flex-start" mt="md" wrap="nowrap">
              <QuestionAnswerResultIcon
                isCorrect={sQuestion.isAnswersCorrect}
              />
              <ContentTitle text={`${index + 1}. ${dQuestion.labelText}`} />
            </Group>
            <Description mt={4} text={GetQuestionInstructionLabel(dQuestion)} />
            {dQuestion.type ==
              CourseDefinitionQuizQuestionTypeEnum.SingleChoice && (
              <SessionAnswerSingleChoice
                disabled={true}
                error={sQuestion.isAnswersCorrect}
                answers={dQuestion.answers ?? []}
                sessionAnswers={sQuestion.answers ?? []}
                onSessionAnswersChange={() => {}}
              />
            )}
            {dQuestion.type ==
              CourseDefinitionQuizQuestionTypeEnum.MultiChoice && (
              <SessionAnswerMultiChoice
                disabled={true}
                error={sQuestion.isAnswersCorrect}
                answers={dQuestion.answers ?? []}
                sessionAnswers={sQuestion.answers ?? []}
                onSessionAnswersChange={() => {}}
              />
            )}
            <SessionAnswerShowCorrectAnswer
              answers={dQuestion.answers ?? []}
              sessionAnswers={sQuestion.answers ?? []}
              isQuestionCorrect={sQuestion.isAnswersCorrect}
              showCorrectResult={enableShowCorrectAnswersInResults}
            />
          </React.Fragment>
        );
      })}

      <Button size="sm" mt="md" onClick={action}>
        Continue
      </Button>
    </Box>
  );
};
